// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, type MedplumClient } from '@medplum/core';
import type { Appointment, Practitioner, Reference } from '@medplum/fhirtypes';

export const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  booked: 'Scheduled',
  fulfilled: 'Completed',
  cancelled: 'Cancelled',
  noshow: 'Patient No-show',
  arrived: 'Arrived',
  'checked-in': 'Checked In',
  rescheduled: 'Rescheduled',
  'technical-issues': 'Technical Issues',
};

export function getAppointmentStatus(appointment: Appointment): string {
  const reason = appointment.cancelationReason?.text?.trim().toLowerCase();
  if (appointment.status === 'cancelled' && reason?.includes('reschedul')) {
    return 'rescheduled';
  }
  if (appointment.status === 'cancelled' && reason?.includes('technical')) {
    return 'technical-issues';
  }
  return appointment.status ?? 'unknown';
}

export function getAppointmentStatusLabel(appointment: Appointment): string {
  const status = getAppointmentStatus(appointment);
  return APPOINTMENT_STATUS_LABELS[status] ?? status;
}

/**
 * Formats an ISO datetime string as a human-readable date.
 * @param isoString - ISO datetime string
 * @returns Formatted date (e.g., "February 20, 2026")
 */
export function formatAppointmentDate(isoString: string | undefined): string {
  if (!isoString) {
    return '—';
  }
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats an ISO datetime as a compact date for table cells, dropping the year
 * when it falls in the current one.
 * @param isoString - ISO datetime string
 * @returns Formatted date (e.g., "Jul 25", or "Dec 30, 2025" in a prior year)
 */
export function formatAppointmentDateShort(isoString: string | undefined): string {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  if (!Number.isFinite(date.getTime())) {
    return '—';
  }
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(
    'en-US',
    sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

/**
 * Formats start/end ISO datetimes as a time range.
 * @param start - ISO datetime string for start
 * @param end - ISO datetime string for end
 * @returns Formatted time range (e.g., "3:30PM–4:00PM")
 */
export function formatAppointmentTime(start: string | undefined, end: string | undefined): string {
  if (!start || !end) {
    return '—';
  }

  const formatTime = (iso: string): string => {
    return new Date(iso)
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(' ', '');
  };

  return `${formatTime(start)}–${formatTime(end)}`;
}

/**
 * Formats a numeric copay amount as currency.
 * @param amount - Copay amount in dollars
 * @returns Formatted currency string (e.g., "$80.00"). Undefined or null → "$0.00".
 */
export function formatCopay(amount: number | undefined): string {
  if (amount === undefined || amount === null) {
    return '$0.00';
  }
  return `$${amount.toFixed(2)}`;
}

/**
 * Extracts the display name for an appointment's type.
 * @param appointment - FHIR Appointment resource
 * @returns Display string from the first appointmentType coding, or 'Appointment'
 */
export function getAppointmentTypeDisplay(appointment: Appointment): string {
  const coding = appointment.appointmentType?.coding?.[0];
  return coding?.display ?? coding?.code ?? 'Appointment';
}

export function isCheckInAppointment(appointment: Appointment): boolean {
  const appointmentType = appointment.appointmentType;
  return Boolean(
    appointmentType?.text?.toLowerCase() === 'check-in' ||
      appointmentType?.coding?.some(
        (coding) => coding.code?.toLowerCase() === 'check-in' || coding.display?.toLowerCase() === 'check-in'
      )
  );
}

type AppointmentParticipantWithLegacyProvider = NonNullable<Appointment['participant']>[number] & {
  individual?: Reference<Practitioner>;
};

function getProviderReference(appointment: Appointment): Reference<Practitioner> | undefined {
  const practitionerParticipant = (
    appointment.participant as AppointmentParticipantWithLegacyProvider[] | undefined
  )?.find(
    (participant) =>
      participant.actor?.reference?.startsWith('Practitioner/') ||
      participant.individual?.reference?.startsWith('Practitioner/')
  );
  return (practitionerParticipant?.actor ?? practitionerParticipant?.individual) as Reference<Practitioner> | undefined;
}

/**
 * Extracts the provider (Practitioner) info from an Appointment resource.
 * @param appointment - FHIR Appointment resource
 * @returns Provider name and ID, or defaults
 */
export function getProviderFromAppointment(appointment: Appointment): { name: string; id: string } {
  const providerReference = getProviderReference(appointment);
  if (!providerReference) {
    return { name: 'Provider', id: '' };
  }

  return {
    name: providerReference.display ?? 'Provider',
    id: providerReference.reference?.replace('Practitioner/', '') ?? '',
  };
}

/** Resolves a provider name when the Appointment reference has no display value. */
export async function resolveProviderName(
  medplum: MedplumClient,
  appointment: Appointment,
  fallbackName: string
): Promise<string> {
  if (fallbackName !== 'Provider') {
    return fallbackName;
  }

  const providerReference = getProviderReference(appointment);
  if (!providerReference?.reference) {
    return fallbackName;
  }

  try {
    const practitioner = (await medplum.readReference(providerReference as Reference<Practitioner>)) as Practitioner;
    const humanName = practitioner.name?.find((name) => formatHumanName(name).trim().length > 0);
    return humanName ? formatHumanName(humanName) : fallbackName;
  } catch {
    return fallbackName;
  }
}

/**
 * Extracts the patient (Patient) info from an Appointment resource.
 * @param appointment - FHIR Appointment resource
 * @returns Patient name and ID, or defaults
 */
export function getPatientFromAppointment(appointment: Appointment): { name: string; id: string } {
  const patientParticipant = appointment.participant?.find((p) => p.actor?.reference?.startsWith('Patient/'));

  if (!patientParticipant?.actor) {
    return { name: 'Patient', id: '' };
  }

  return {
    name: patientParticipant.actor.display ?? 'Patient',
    id: patientParticipant.actor.reference?.replace('Patient/', '') ?? '',
  };
}
